/**
 * Fendertex calculator — UI module.
 *
 * Builds the calculator inside a container element. Wire-up only; styling
 * lives in fender-calculator-ui.css; sizing rules in fender-calculator-engine.js;
 * commerce wiring in fender-calculator-products.js.
 *
 *   window.FenderCalculator.mount(container);
 *
 * Detects storefront context via window.Shopify; outside the storefront,
 * fetches product data from https://fendertex.nl (CORS open) and stubs cart-add.
 */
(function () {
  const { recommend } = window.FenderEngine;
  const FP = window.FenderProducts;

  const M_PER_FT = 0.3048;
  const CM_PER_IN = 2.54;

  function isStorefront() { return typeof window !== 'undefined' && !!window.Shopify; }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, function (ch) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
    });
  }

  function mount(container) {
    if (typeof container === 'string') container = document.querySelector(container);
    if (!container) throw new Error('FenderCalculator.mount: container not found');

    container.innerHTML = TEMPLATE;
    container.classList.add('fc-root');

    const els = {
      boat:           container.querySelector('[data-role="boat"]'),
      free:           container.querySelector('[data-role="free"]'),
      boatDisplay:    container.querySelector('[data-role="boat-display"]'),
      freeDisplay:    container.querySelector('[data-role="free-display"]'),
      unitM:          container.querySelector('[data-action="unit-m"]'),
      unitFt:         container.querySelector('[data-action="unit-ft"]'),
      recommendation: container.querySelector('[data-role="recommendation"]'),
      configurator:   container.querySelector('[data-role="configurator"]'),
      colours:        container.querySelector('[data-role="colours"]'),
      items:          container.querySelector('[data-role="items"]'),
      summary:        container.querySelector('[data-role="summary"]'),
      total:          container.querySelector('[data-role="total"]'),
      actionBtn:      container.querySelector('[data-action="action"]'),
      bespokeCta:     container.querySelector('[data-role="bespoke-cta"]'),
      status:         container.querySelector('[data-role="status"]'),
    };

    const state = {
      boatLength: 14,
      freeboard: 120,
      unitSystem: 'metric',
      recommendation: null,
      selectedColour: 'grey',
      // Per-row quantities (retained even when the row is unchecked).
      qty: { cCover: 0, clsCover: 0, sCover: 0, rope: 0, dRing: 0, pumpGm5: 1, pumpGe10: 0 },
      // Per-row inclusion (checkbox). Lets users untick without zeroing the counter.
      included: { cCover: true, clsCover: false, sCover: true, rope: true, dRing: false, pumpGm5: true, pumpGe10: false },
      configuratorOpen: false,
      itemPrices: {},
      fetchToken: 0,
    };

    // Colour-grouped accessory → product handle, with black fallback for the
    // accessories the client wants always orderable (rope, D-Ring).
    function resolveHandle(kind, colour) {
      // Single-product (not colour-grouped) accessories.
      if (kind === 'pumpGm5' || kind === 'pumpGe10') return FP.HANDLES[kind];
      const map = FP.HANDLES[kind];
      if (!map) return null;
      if (kind === 'rope' || kind === 'dRing') return map[colour] || map['black'] || null;
      return map[colour] || null;
    }

    function syncAccessoryDefaults() {
      const r = state.recommendation;
      if (!r || r.status !== 'ok') return;
      const totalFenders = r.cylindrical.qty + (r.spherical ? r.spherical.qty : 0);
      const electric = state.boatLength > 12;
      // Quantities are pre-filled to sensible amounts so a row is ready the
      // moment it's ticked — inclusion is controlled by the checkbox, not qty.
      state.qty.cCover   = r.cylindrical.qty;
      state.qty.clsCover = r.cylindrical.qty;
      state.qty.sCover   = r.spherical ? r.spherical.qty : 0;
      state.qty.rope     = totalFenders;
      state.qty.dRing    = totalFenders;
      state.qty.pumpGm5  = 1;
      state.qty.pumpGe10 = 1;
      // Default ticks: covers + rope on; D-Ring + CLS opt-in; pump by boat length.
      state.included = {
        cCover:   true,
        clsCover: false,
        sCover:   true,
        rope:     true,
        dRing:    false,
        pumpGm5:  !electric,
        pumpGe10: electric,
      };
    }

    // ===== Slider formatting / steps =====
    function formatBoat(metres) {
      if (state.unitSystem === 'metric') return Math.round(metres) + ' m';
      return Math.round(metres / M_PER_FT) + ' ft';
    }
    function formatFreeboard(cm) {
      if (state.unitSystem === 'metric') return Math.round(cm) + ' cm';
      return Math.round(cm / CM_PER_IN) + ' in';
    }
    function applySliderSteps() {
      if (state.unitSystem === 'metric') { els.boat.step = '1'; els.free.step = '5'; }
      else { els.boat.step = String(M_PER_FT); els.free.step = String(CM_PER_IN); }
      const bStep = parseFloat(els.boat.step);
      const fStep = parseFloat(els.free.step);
      els.boat.value = Math.round(parseFloat(els.boat.value) / bStep) * bStep;
      els.free.value = Math.round(parseFloat(els.free.value) / fStep) * fStep;
      updateRangeFill(els.boat);
      updateRangeFill(els.free);
      updateBounds();
    }
    function updateRangeFill(input) {
      const min = parseFloat(input.min), max = parseFloat(input.max), val = parseFloat(input.value);
      input.style.setProperty('--fc-pct', ((val - min) / (max - min)) * 100 + '%');
    }
    function updateBounds() {
      const boatLo = container.querySelector('[data-role="boat-bounds"] span:first-child');
      const boatHi = container.querySelector('[data-role="boat-bounds"] span:last-child');
      const freeLo = container.querySelector('[data-role="free-bounds"] span:first-child');
      const freeHi = container.querySelector('[data-role="free-bounds"] span:last-child');
      if (!boatLo || !freeLo) return;
      const bMin = parseFloat(els.boat.min), bMax = parseFloat(els.boat.max);
      const fMin = parseFloat(els.free.min), fMax = parseFloat(els.free.max);
      if (state.unitSystem === 'metric') {
        boatLo.textContent = bMin + 'm'; boatHi.textContent = bMax + 'm';
        freeLo.textContent = fMin + 'cm'; freeHi.textContent = fMax + 'cm';
      } else {
        boatLo.textContent = Math.round(bMin / M_PER_FT) + ' ft';
        boatHi.textContent = Math.round(bMax / M_PER_FT) + ' ft';
        freeLo.textContent = Math.round(fMin / CM_PER_IN) + ' in';
        freeHi.textContent = Math.round(fMax / CM_PER_IN) + ' in';
      }
    }

    // ===== Engine =====
    function applyEngine() {
      state.boatLength = parseFloat(els.boat.value);
      state.freeboard  = parseFloat(els.free.value);
      state.recommendation = recommend(state.boatLength, state.freeboard);
      syncAccessoryDefaults();
    }

    function catalogueIndex(modelCode) {
      const list = window.FenderEngine.CYLINDRICAL;
      for (let i = 0; i < list.length; i++) if (list[i].model === modelCode) return i;
      return -1;
    }

    function renderRecommendation() {
      els.boatDisplay.textContent = formatBoat(state.boatLength);
      els.freeDisplay.textContent = formatFreeboard(state.freeboard);
      updateRangeFill(els.boat);
      updateRangeFill(els.free);

      const r = state.recommendation;
      if (r.status === 'bespoke') {
        els.recommendation.innerHTML =
          '<div class="fc-bespoke">' +
            '<h3>Bespoke recommendation needed</h3>' +
            '<p>' + escapeHTML(r.bespokeReason) + '</p>' +
            '<p>For boats over 30 m, every setup is configured individually. Tell us about your vessel and we\'ll send a tailored proposal.</p>' +
          '</div>';
        els.configurator.hidden = true;
        els.summary.hidden = true;
        els.bespokeCta.hidden = false;
        state.configuratorOpen = false;
        return;
      }

      const c = r.cylindrical;
      let html =
        '<h3 class="fc-reco-heading">Your Recommended Setup</h3>' +
        '<div class="fc-reco-body">' +
          '<ul class="fc-reco-checklist">';
      if (r.spherical) {
        const s = r.spherical;
        html += '<li>' + tickSvg() + '<span><strong>' + s.qty + 'x ' + s.model + '</strong> Spherical Fenders</span></li>';
      }
      html +=
          '<li>' + tickSvg() + '<span><strong>' + c.qty + 'x ' + c.model + '</strong> Cylindrical Fenders</span></li>' +
          '</ul>' +
          '<div class="fc-reco-images">';
      html += '<figure class="fc-reco-fig fc-reco-fig--cyl"><img data-role="img-cyl" alt="' + c.model + ' cylindrical fender" loading="lazy"></figure>';
      if (r.spherical) {
        html += '<figure class="fc-reco-fig fc-reco-fig--sph"><img data-role="img-sph" alt="' + r.spherical.model + ' spherical fender" loading="lazy"></figure>';
      }
      html += '</div></div>';
      els.recommendation.innerHTML = html;

      els.summary.hidden = false;
      els.bespokeCta.hidden = true;
      els.configurator.hidden = !state.configuratorOpen;
      els.actionBtn.textContent = state.configuratorOpen ? 'Order Now' : 'Customize Now';

      applyRecommendationImages(r);
      if (state.configuratorOpen) { renderColours(); renderItems(); }
      scheduleRefreshPrices();   // compute total in BOTH collapsed and expanded states
    }

    function tickSvg() {
      return '<span class="fc-tick" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M1.5 5.5L4 8L8.5 2.5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></span>';
    }

    // ===== Images =====
    async function applyRecommendationImages(r) {
      if (!r || r.status !== 'ok') return;
      const colour = state.selectedColour;
      const tasks = [applyImage('cylindrical', colour, '[data-role="img-cyl"]')];
      if (r.spherical) tasks.push(applyImage('spherical', colour, '[data-role="img-sph"]'));
      await Promise.all(tasks);
    }
    async function applyImage(kind, colour, selector) {
      const handle = FP.HANDLES[kind][colour];
      if (!handle) return;
      try {
        const product = await FP.fetchProduct(handle);
        const url = product.featured_image || (product.images && product.images[0]);
        if (!url) return;
        const img = container.querySelector(selector);
        if (img) img.src = url;
      } catch (err) { console.warn('applyImage ' + kind + '/' + colour, err); }
    }
    async function applyThumbnail(kind, selector) {
      const handle = resolveHandle(kind, state.selectedColour);
      if (!handle) return;
      try {
        const product = await FP.fetchProduct(handle);
        const url = product.featured_image || (product.images && product.images[0]);
        if (!url) return;
        const img = container.querySelector(selector);
        if (img) img.src = url;
      } catch (err) { /* silent */ }
    }

    // ===== Colours =====
    function renderColours() {
      els.colours.innerHTML = FP.PALETTE.map(function (c) {
        const active = c.key === state.selectedColour ? ' is-active' : '';
        return '<button type="button" class="fc-colour-swatch' + active + '" data-action="pick-colour" data-colour="' + c.key + '" aria-label="' + c.label + '" title="' + c.label + '" style="background:' + c.hex + '"></button>';
      }).join('');
    }

    // ===== Items list (CLS-Cover intentionally excluded) =====
    function activeAccessories() {
      const r = state.recommendation;
      if (!r || r.status !== 'ok') return [];
      const cIdx = catalogueIndex(r.cylindrical.model);
      const rows = [];
      rows.push({ kind: 'cCover', label: 'C-Cover (' + r.cylindrical.model + ')' });
      // CLS-Cover sits right below the C-Cover, opt-in (qty 0), only for the
      // larger cylindrical fenders C145+ (the only sizes CLS is made in).
      if (cIdx >= 5) rows.push({ kind: 'clsCover', label: 'CLS-Cover (' + r.cylindrical.model + ')' });
      if (r.spherical) rows.push({ kind: 'sCover', label: 'S-Cover (' + r.spherical.model + ')' });
      rows.push({ kind: 'rope',  label: 'Rope (spliced)' });
      rows.push({ kind: 'dRing', label: 'Sheated D-Ring' });
      rows.push({ kind: 'pumpGm5',  label: 'Manual Pump GM-5' });
      rows.push({ kind: 'pumpGe10', label: 'GE-10 Electric Pump' });
      return rows;
    }

    function renderItems() {
      const r = state.recommendation;
      if (!r || r.status !== 'ok') { els.items.innerHTML = ''; return; }
      const accessories = activeAccessories();
      els.items.innerHTML = accessories.map(stepperRow).join('');
      accessories.forEach(function (row) {
        applyThumbnail(row.kind, '[data-role="thumb-' + row.kind + '"]');
      });
    }

    function stepperRow(row) {
      const handle = resolveHandle(row.kind, state.selectedColour);
      const unavailable = !handle;
      const cls = unavailable ? ' fc-item--unavailable' : '';
      const qty = unavailable ? 0 : state.qty[row.kind];
      const disabledAttr = unavailable ? ' disabled' : '';
      const checked = state.included[row.kind] ? ' checked' : '';
      return (
        '<div class="fc-item' + cls + '" data-kind="' + row.kind + '">' +
          '<input type="checkbox" class="fc-item-check" data-action="toggle-include" data-kind="' + row.kind + '"' + checked + disabledAttr + ' aria-label="Include ' + escapeHTML(row.label) + '">' +
          '<span class="fc-stepper">' +
            '<button type="button" data-action="qty-dec" data-kind="' + row.kind + '" aria-label="Decrease"' + disabledAttr + '>–</button>' +
            '<span data-role="qty-' + row.kind + '">' + qty + '</span>' +
            '<button type="button" data-action="qty-inc" data-kind="' + row.kind + '" aria-label="Increase"' + disabledAttr + '>+</button>' +
          '</span>' +
          '<span class="fc-item-thumb" data-action="view-product" data-kind="' + row.kind + '" role="button" tabindex="0" title="View product details"><img data-role="thumb-' + row.kind + '" alt="" loading="lazy"></span>' +
          '<span class="fc-item-name" data-action="view-product" data-kind="' + row.kind + '" role="button" tabindex="0">' + escapeHTML(row.label) +
            (unavailable ? '<span class="fc-note">not available in this colour</span>' : '') +
          '</span>' +
          '<span class="fc-item-price" data-role="price-' + row.kind + '">—</span>' +
        '</div>'
      );
    }

    // ===== Live prices + total =====
    function scheduleRefreshPrices() {
      const token = ++state.fetchToken;
      clearTimeout(scheduleRefreshPrices._t);
      scheduleRefreshPrices._t = setTimeout(function () {
        refreshPrices(token).catch(function (err) { console.warn('refreshPrices', err); });
      }, 100);
    }

    async function refreshPrices(token) {
      const items = collectItems();
      const resolved = await Promise.all(items.map(function (i) { return resolveItem(i).catch(function () { return null; }); }));
      if (token !== state.fetchToken) return;
      state.itemPrices = {};
      let currency = 'EUR';
      resolved.forEach(function (info) {
        if (!info) return;
        currency = info.currency || currency;
        state.itemPrices[info.kind] = info;
      });
      // Per-row prices (only present when the configurator is open)
      items.forEach(function (item) {
        const info = state.itemPrices[item.kind];
        const node = container.querySelector('[data-role="price-' + item.kind + '"]');
        if (!node) return;
        if (!info || !item.included) { node.textContent = item.included ? '—' : ''; return; }
        node.textContent = FP.priceCentsToString(info.unitPrice * item.qty, info.currency);
      });
      // Total. Before "Customize Now" is clicked, only the fenders count;
      // accessories are added to the total once the configurator is open.
      let total = 0;
      items.forEach(function (item) {
        if (!item.included) return;
        const isPrimary = item.kind === 'cylindrical' || item.kind === 'spherical';
        if (!state.configuratorOpen && !isPrimary) return;
        const info = state.itemPrices[item.kind];
        if (info) total += info.unitPrice * item.qty;
      });
      els.total.textContent = FP.priceCentsToString(total, currency);
    }

    function collectItems() {
      const r = state.recommendation;
      if (!r || r.status !== 'ok') return [];
      const colour = state.selectedColour;
      const cIdx = catalogueIndex(r.cylindrical.model);
      const out = [];
      out.push({ kind: 'cylindrical', handle: FP.HANDLES.cylindrical[colour], model: r.cylindrical.model, qty: r.cylindrical.qty, included: true, picker: 'model' });
      if (r.spherical) {
        out.push({ kind: 'spherical', handle: FP.HANDLES.spherical[colour], model: r.spherical.model, qty: r.spherical.qty, included: true, picker: 'model' });
      }
      const accSpec = [
        { kind: 'cCover', handle: resolveHandle('cCover', colour), model: r.cylindrical.model, picker: 'model' },
      ];
      if (cIdx >= 5) accSpec.push({ kind: 'clsCover', handle: resolveHandle('clsCover', colour), model: r.cylindrical.model, picker: 'model' });
      if (r.spherical) accSpec.push({ kind: 'sCover', handle: resolveHandle('sCover', colour), model: r.spherical.model, picker: 'model' });
      accSpec.push({ kind: 'rope',  handle: resolveHandle('rope', colour),  picker: 'rope' });
      accSpec.push({ kind: 'dRing', handle: resolveHandle('dRing', colour), picker: 'sole' });
      accSpec.push({ kind: 'pumpGm5',  handle: FP.HANDLES.pumpGm5,  picker: 'sole' });
      accSpec.push({ kind: 'pumpGe10', handle: FP.HANDLES.pumpGe10, picker: 'sole' });
      accSpec.forEach(function (a) {
        a.qty = state.qty[a.kind];
        a.included = !!state.included[a.kind] && a.qty > 0 && !!a.handle;
        out.push(a);
      });
      return out;
    }

    async function resolveItem(item) {
      if (!item.handle) return null;
      const product = await FP.fetchProduct(item.handle);
      let variant = null;
      if (item.picker === 'model') variant = FP.pickVariantByModelCode(product, item.model);
      else if (item.picker === 'rope') variant = FP.pickRopeVariant(product, FP.ROPE_DEFAULT_LENGTH, FP.ROPE_DEFAULT_LOOP);
      else variant = FP.pickSoleVariant(product);
      if (!variant) return null;
      return { kind: item.kind, variantId: variant.id, unitPrice: variant.price, currency: 'EUR', available: variant.available !== false };
    }

    // ===== Cart add =====
    async function orderNow() {
      const items = collectItems().filter(function (i) { return i.included; });
      const resolved = await Promise.all(items.map(function (i) { return resolveItem(i); }));
      const lineItems = [];
      items.forEach(function (item, idx) {
        const info = resolved[idx];
        if (info && info.variantId) lineItems.push({ id: info.variantId, quantity: item.qty });
      });
      if (!lineItems.length) { setStatus('Nothing to add to cart.'); return; }
      if (!isStorefront()) {
        alert('(Demo) Would POST /cart/add.js with:\n\n' + lineItems.map(function (li) { return '  variant ' + li.id + ' × ' + li.quantity; }).join('\n'));
        return;
      }
      setStatus('Adding to cart…');
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: lineItems }),
        });
        if (!res.ok) throw new Error('cart/add ' + res.status + ': ' + (await res.text()).slice(0, 200));
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
        setStatus('Added to cart.');
      } catch (err) {
        console.error(err);
        setStatus('Could not add to cart. ' + err.message);
      }
    }

    function setStatus(msg) { if (els.status) els.status.textContent = msg || ''; }

    function bumpRange(input, dir) {
      const min = parseFloat(input.min), max = parseFloat(input.max), step = parseFloat(input.step) || 1;
      input.value = Math.max(min, Math.min(max, parseFloat(input.value) + dir * step));
      input.dispatchEvent(new Event('input'));
    }

    // ===== Events =====
    els.boat.addEventListener('input', function () { applyEngine(); renderRecommendation(); });
    els.free.addEventListener('input', function () { applyEngine(); renderRecommendation(); });
    els.unitM.addEventListener('click', function () { setUnit('metric'); });
    els.unitFt.addEventListener('click', function () { setUnit('imperial'); });

    function setUnit(next) {
      state.unitSystem = next;
      els.unitM.classList.toggle('is-active', next === 'metric');
      els.unitFt.classList.toggle('is-active', next === 'imperial');
      applySliderSteps();
      applyEngine();
      renderRecommendation();
    }

    // Single action button: "Customize Now" expands the configurator; once open
    // it becomes "Order Now" and adds to cart.
    els.actionBtn.addEventListener('click', function () {
      if (!state.configuratorOpen) {
        state.configuratorOpen = true;
        els.configurator.hidden = false;
        els.actionBtn.textContent = 'Order Now';
        renderColours();
        renderItems();
        scheduleRefreshPrices();
      } else {
        orderNow();
      }
    });

    container.addEventListener('click', function (e) {
      const t = e.target.closest('[data-action]');
      if (!t) return;
      const action = t.dataset.action;
      if (action === 'pick-colour') {
        state.selectedColour = t.dataset.colour;
        renderColours();
        renderItems();
        applyRecommendationImages(state.recommendation);
        scheduleRefreshPrices();
      } else if (action === 'qty-dec' || action === 'qty-inc') {
        const kind = t.dataset.kind;
        const dir = action === 'qty-inc' ? 1 : -1;
        state.qty[kind] = Math.max(0, Math.min(99, (state.qty[kind] || 0) + dir));
        const q = container.querySelector('[data-role="qty-' + kind + '"]');
        if (q) q.textContent = state.qty[kind];
        scheduleRefreshPrices();
      } else if (action === 'toggle-include') {
        const kind = t.dataset.kind;
        state.included[kind] = t.checked;
        scheduleRefreshPrices();
      } else if (action === 'view-product') {
        openProductModal(t.dataset.kind);
      } else if (action === 'boat-dec') { bumpRange(els.boat, -1); }
        else if (action === 'boat-inc') { bumpRange(els.boat, +1); }
        else if (action === 'free-dec') { bumpRange(els.free, -1); }
        else if (action === 'free-inc') { bumpRange(els.free, +1); }
    });

    // Keyboard activation for the clickable name/thumbnail (role=button).
    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      const t = e.target.closest('[data-action="view-product"]');
      if (!t) return;
      e.preventDefault();
      openProductModal(t.dataset.kind);
    });

    // Open the shared product modal with the product behind a given accessory row.
    async function openProductModal(kind) {
      const handle = resolveHandle(kind, state.selectedColour);
      if (!handle) return;
      try {
        const product = await FP.fetchProduct(handle);
        FCModal.show(product);
      } catch (err) { console.warn('openProductModal ' + kind, err); }
    }

    // ===== First render =====
    applySliderSteps();
    applyEngine();
    renderRecommendation();

    return { state, els };
  }

  // ===== Trust strip SVG icons =====
  const ICON_TRUCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10 17h4V5H2v12h3"/><path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h2"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="17.5" cy="17.5" r="2.5"/></svg>';
  const ICON_SHIELD = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg>';
  const ICON_PACKAGE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M16.5 9.4 7.55 4.24"/><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="m3.3 7 8.7 5 8.7-5"/><path d="M12 22V12"/></svg>';

  // ===== Shared product-detail modal (carousel + description) =====
  // Appended to <body> so it isn't clipped by the calculator's `contain` box.
  const FCModal = (function () {
    let el, imgEl, titleEl, descEl, counterEl, prevBtn, nextBtn;
    let images = [], idx = 0;

    function ensure() {
      if (el) return;
      el = document.createElement('div');
      el.className = 'fc-modal';
      el.hidden = true;
      el.innerHTML =
        '<div class="fc-modal__backdrop" data-action="fc-modal-close"></div>' +
        '<div class="fc-modal__dialog" role="dialog" aria-modal="true">' +
          '<button type="button" class="fc-modal__close" data-action="fc-modal-close" aria-label="Close">&times;</button>' +
          '<div class="fc-modal__media">' +
            '<button type="button" class="fc-modal__nav fc-modal__nav--prev" data-action="fc-modal-prev" aria-label="Previous image">&lsaquo;</button>' +
            '<img class="fc-modal__img" data-role="fc-modal-img" alt="">' +
            '<button type="button" class="fc-modal__nav fc-modal__nav--next" data-action="fc-modal-next" aria-label="Next image">&rsaquo;</button>' +
            '<div class="fc-modal__counter" data-role="fc-modal-counter"></div>' +
          '</div>' +
          '<div class="fc-modal__text">' +
            '<h3 class="fc-modal__title" data-role="fc-modal-title"></h3>' +
            '<div class="fc-modal__desc" data-role="fc-modal-desc"></div>' +
          '</div>' +
        '</div>';
      document.body.appendChild(el);
      imgEl     = el.querySelector('[data-role="fc-modal-img"]');
      titleEl   = el.querySelector('[data-role="fc-modal-title"]');
      descEl    = el.querySelector('[data-role="fc-modal-desc"]');
      counterEl = el.querySelector('[data-role="fc-modal-counter"]');
      prevBtn   = el.querySelector('.fc-modal__nav--prev');
      nextBtn   = el.querySelector('.fc-modal__nav--next');

      el.addEventListener('click', function (e) {
        const a = e.target.closest('[data-action]');
        if (!a) return;
        const act = a.dataset.action;
        if (act === 'fc-modal-close') close();
        else if (act === 'fc-modal-prev') step(-1);
        else if (act === 'fc-modal-next') step(1);
      });
      document.addEventListener('keydown', function (e) {
        if (el.hidden) return;
        if (e.key === 'Escape') close();
        else if (e.key === 'ArrowLeft') step(-1);
        else if (e.key === 'ArrowRight') step(1);
      });
    }

    function render() {
      const has = images.length > 0;
      imgEl.style.display = has ? '' : 'none';
      if (has) imgEl.src = images[idx];
      counterEl.textContent = images.length > 1 ? (idx + 1) + ' / ' + images.length : '';
      const multi = images.length > 1;
      prevBtn.style.display = multi ? '' : 'none';
      nextBtn.style.display = multi ? '' : 'none';
    }
    function step(d) {
      if (images.length < 2) return;
      idx = (idx + d + images.length) % images.length;
      render();
    }
    function close() {
      if (!el) return;
      el.hidden = true;
      document.documentElement.style.overflow = '';
    }
    function show(product) {
      ensure();
      images = (product.images && product.images.length)
        ? product.images.slice()
        : (product.featured_image ? [product.featured_image] : []);
      idx = 0;
      titleEl.textContent = product.title || '';
      descEl.innerHTML = product.description || '';
      render();
      el.hidden = false;
      document.documentElement.style.overflow = 'hidden';
    }

    return { show: show };
  })();

  // ===== HTML template =====
  const TEMPLATE = (
    '<div class="fc-card">' +
      '<div class="fc-unit-pill-wrap">' +
        '<div class="fc-unit-pill" role="tablist">' +
          '<button type="button" class="is-active" data-action="unit-m">Meters</button>' +
          '<button type="button" data-action="unit-ft">Feet/Inches</button>' +
        '</div>' +
      '</div>' +

      '<div class="fc-row">' +
        '<div class="fc-row-header">' +
          '<div class="fc-row-title">' +
            '<strong>Boat Length</strong>' +
            '<span class="fc-sublabel">This will determine the amount of fenders you need.</span>' +
          '</div>' +
          '<div class="fc-stepper-inline">' +
            '<button type="button" data-action="boat-dec" aria-label="Decrease">–</button>' +
            '<span class="fc-value" data-role="boat-display">14 m</span>' +
            '<button type="button" data-action="boat-inc" aria-label="Increase">+</button>' +
          '</div>' +
        '</div>' +
        '<input type="range" data-role="boat" min="0" max="50" step="1" value="14">' +
        '<div class="fc-range-bounds" data-role="boat-bounds"><span>0m</span><span>50m</span></div>' +
      '</div>' +

      '<div class="fc-row">' +
        '<div class="fc-row-header">' +
          '<div class="fc-row-title">' +
            '<strong>Freeboard Height</strong>' +
            '<span class="fc-sublabel">To ensure your fender covers the recommended 2/3 of your freeboard.</span>' +
          '</div>' +
          '<div class="fc-stepper-inline">' +
            '<button type="button" data-action="free-dec" aria-label="Decrease">–</button>' +
            '<span class="fc-value" data-role="free-display">120 cm</span>' +
            '<button type="button" data-action="free-inc" aria-label="Increase">+</button>' +
          '</div>' +
        '</div>' +
        '<input type="range" data-role="free" min="40" max="400" step="5" value="120">' +
        '<div class="fc-range-bounds" data-role="free-bounds"><span>40cm</span><span>400cm</span></div>' +
      '</div>' +

      '<div class="fc-recommendation" data-role="recommendation" aria-live="polite"></div>' +

      '<div class="fc-configurator" data-role="configurator" hidden>' +
        '<section class="fc-config-section">' +
          '<h3>Pick a Color</h3>' +
          '<div class="fc-colours" data-role="colours"></div>' +
        '</section>' +
        '<section class="fc-config-section fc-options-card">' +
          '<div class="fc-options-header"><h3>Recommended options</h3></div>' +
          '<div class="fc-items" data-role="items"></div>' +
        '</section>' +
      '</div>' +

      '<div class="fc-summary" data-role="summary">' +
        '<div class="fc-total-row"><span>Total</span><strong data-role="total">—</strong></div>' +
        '<button type="button" class="fc-order-btn" data-action="action">Customize Now</button>' +
        '<div class="fc-status" data-role="status" aria-live="polite"></div>' +
      '</div>' +

      '<div class="fc-primary-actions" data-role="bespoke-cta" hidden>' +
        '<a class="fc-button" href="/pages/contact">Contact us</a>' +
      '</div>' +

      '<div class="fc-trust-strip">' +
        '<div>' + ICON_TRUCK + '<span>Free EU shipping</span></div>' +
        '<div>' + ICON_SHIELD + '<span>3-year warranty</span></div>' +
        '<div>' + ICON_PACKAGE + '<span>6-8 week lead time</span></div>' +
      '</div>' +
    '</div>'
  );

  const FenderCalculator = { mount };
  if (typeof window !== 'undefined') window.FenderCalculator = FenderCalculator;
})();
